import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import {
  isCrmLicense,
  formatLicenseInfo,
  specialtyLineDisplay,
  getProfessionalCrmSuffix,
  isAnnounceableSpecialty,
  MAX_DISPLAYED_SPECIALTIES,
} from '@/utils/professional/professional-license.util';
import {
  MainPostGraduation,
  formatMainPostGraduationTitle,
  formatOtherPostGraduations,
} from '@/utils/professional/main-post-graduation.util';
import { Link } from '@/components/ui/link/link.component';
import { SchedulesSearchProfessionalLicenses } from '@/app/(main)/(pages)/busca/[slug]/_types/search-partner-data.type';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';

interface SearchEntitySpecialtiesProps {
  professional: {
    licenses: SchedulesSearchProfessionalLicenses[];
    gender?: string;
    honorific?: string;
    mainPostGraduation?: MainPostGraduation | null;
  };
  formationsHref?: string;
}

export const EntitySpecialtiesAndRqe = ({ professional, formationsHref }: SearchEntitySpecialtiesProps) => {
  const { licenses, gender, honorific, mainPostGraduation } = professional;

  const validLicenses = licenses.filter((license) => license.professionalLicenseRecord);

  if (validLicenses.length === 0) {
    return null;
  }

  const registration = validLicenses[0];
  const isCrm = isCrmLicense(registration);
  const crmSuffix = isCrm ? getProfessionalCrmSuffix({ gender, honorific }) : undefined;
  const displayedSpecialties = isCrm
    ? validLicenses
        .filter((license) =>
          license.isDisplayed && isAnnounceableSpecialty(license.specialtyName, license.specialistQualificationRecord))
        .slice(0, MAX_DISPLAYED_SPECIALTIES)
    : [];
  const announcedPostGraduation = isCrm && displayedSpecialties.length === 0 ? mainPostGraduation : null;

  return (
    <div className="flex-grow" data-testid={DataTestIdEnum.ENTITY_SPECIALTIES_AND_RQE}>
      <Typography
        variant={TypographyVariant.MEDIUM_BODY_TEXT}
        className="text-gray-700"
        data-testid={DataTestIdEnum.ENTITY_SPECIALTIES_AND_RQE_LINE}
      >
        {formatLicenseInfo(registration, crmSuffix)}
      </Typography>
      {displayedSpecialties.map((license) => (
        <Typography
          variant={TypographyVariant.MEDIUM_BODY_TEXT}
          className="text-gray-700"
          data-testid={DataTestIdEnum.ENTITY_SPECIALTIES_AND_RQE_LINE}
          key={`${license.professionalLicenseRecord}-${license.specialtyName}`}
        >
          {specialtyLineDisplay(license)}
        </Typography>
      ))}
      {announcedPostGraduation && (
        <Typography
          variant={TypographyVariant.MEDIUM_BODY_TEXT}
          className="text-gray-700"
          data-testid={DataTestIdEnum.ENTITY_MAIN_POST_GRADUATION}
        >
          {`${formatMainPostGraduationTitle(announcedPostGraduation, { gender, honorific })} • NÃO ESPECIALISTA`}
          {announcedPostGraduation.othersCount > 0 && (
            <>
              {' '}
              {formationsHref
                ? (
                    <Link
                      href={formationsHref}
                      className="whitespace-nowrap font-semibold text-green-500 hover:underline"
                      data-testid={DataTestIdEnum.ENTITY_OTHER_POST_GRADUATIONS_LINK}
                    >
                      {formatOtherPostGraduations(announcedPostGraduation.othersCount)}
                    </Link>
                  )
                : <span className="whitespace-nowrap font-semibold text-green-500">{formatOtherPostGraduations(announcedPostGraduation.othersCount)}</span>}
            </>
          )}
        </Typography>
      )}
    </div>
  );
};
